import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  CreditCard,
  Wallet,
  Wifi,
  HeadphonesIcon,
  Ticket,
  RotateCcw,
  ArrowDown,
  ArrowRightLeft,
  HelpCircle,
  Sparkles,
  TrendingUp,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  Zap,
  Clock,
} from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  type?: 'text' | 'quick-reply' | 'contact' | 'welcome' | 'ticket-suggestion' | 'session-expired';
  actions?: ChatAction[];
}

interface QuickAction {
  id: string;
  label: string;
  message: string;
  icon: React.ReactNode;
  category: 'operations' | 'analytics' | 'account';
}

interface ChatAction {
  label: string;
  href?: string;
  onClick?: () => void;
  variant: 'primary' | 'secondary';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'moko_chat_history';
const MAX_STORED_MESSAGES = 50;

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------
const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const isTransactionMessage = (text: string) =>
  text.includes('Transaction Overview') ||
  (text.includes('Amount:') && text.includes('Status:') && text.includes('References:'));

const extractTransactionField = (text: string, field: string): string => {
  for (const line of text.split('\n')) {
    if (line.includes(field)) {
      return line.split(field)[1]?.trim() || '—';
    }
  }
  return '—';
};

const extractTip = (text: string): string => {
  for (const marker of ['💡', '⚠️', '✅']) {
    if (text.includes(marker)) {
      return text.split(marker)[1]?.split('\n')[0]?.trim() || '';
    }
  }
  return '';
};

const getStatusStyles = (text: string) => {
  const status = extractTransactionField(text, 'Status:');
  if (status.includes('Reussie') || status.includes('Successful') || status.includes('✅'))
    return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
  if (status.includes('Echouee') || status.includes('Failed') || status.includes('❌'))
    return 'bg-red-500/10 text-red-400 border-red-500/30';
  if (status.includes('attente') || status.includes('Pending') || status.includes('Submitted') || status.includes('⏳') || status.includes('📤'))
    return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
  return 'bg-white/5 text-white/60 border-white/10';
};

const requiresTicket = (userMsg: string, botReply: string): boolean => {
  const uLower = userMsg.toLowerCase();
  const bLower = botReply.toLowerCase();

  if (
    bLower.includes("pas compris") ||
    bLower.includes("didn't quite understand") ||
    bLower.includes("contactez") ||
    bLower.includes("contact support") ||
    bLower.includes("ouvrir un ticket") ||
    bLower.includes("open a ticket")
  )
    return true;

  const escalation = [
    'bug', 'error', 'erreur', 'probleme', 'problem', 'issue',
    'not working', 'broken', 'panne', 'bloque', 'complaint', 'reclamation',
  ];
  return escalation.some((kw) => uLower.includes(kw));
};

const detectResponseType = (reply: string): string | null => {
  if (reply.includes('Transaction Overview')) return 'transaction';
  if (reply.includes('Solde du Compte') || reply.includes('Account Balance')) return 'balance';
  if (reply.includes('Performance Reseau') || reply.includes('Network Performance')) return 'network';
  if (reply.includes('Transferts') || reply.includes('Transfer')) return 'transfer';
  return null;
};

/**
 * Detects if a user input looks like a transaction reference (single token,
 * 8+ alphanumeric chars, no spaces). Used to route to /bot/lookup instead of /chat/message.
 */
const looksLikeReference = (text: string): boolean => {
  const trimmed = text.trim();
  if (trimmed.includes(' ')) return false;
  if (trimmed.length < 8) return false;
  // Allow alphanumeric, dashes and underscores (covers UUIDs, paydrc refs, switch refs, etc.)
  return /^[a-zA-Z0-9_-]+$/.test(trimmed);
};

/**
 * Formats a /bot/lookup response into a Markdown message similar to the Telegram bot.
 */
const formatLookupResponse = (data: any, language: string): string => {
  if (!data || data.found === false) {
    return language === 'fr'
      ? `**MOKO AFRIKA** | Recherche de transaction\n━━━━━━━━━━━━━━━━━━━━━\n\nAucune transaction trouvée pour la référence \`${data?.reference || ''}\` sur votre compte.\n\nVérifiez la référence et réessayez, ou ouvrez un ticket si le problème persiste.`
      : `**MOKO AFRIKA** | Transaction Lookup\n━━━━━━━━━━━━━━━━━━━━━\n\nNo transaction found for reference \`${data?.reference || ''}\` under your account.\n\nDouble-check the reference and try again, or open a ticket if the issue persists.`;
  }

  const t = data.transaction;
  const initiated = t.created_at ? new Date(t.created_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US') : 'N/A';

  const lines: string[] = [];
  lines.push(`**MOKO AFRIKA** | Transaction Status`);
  lines.push('━━━━━━━━━━━━━━━━━━━━━');
  lines.push(`**Status:** ${t.status || 'Unknown'} ${t.status_emoji || ''}`);
  lines.push(`**Type:** ${t.type || 'N/A'}`);
  lines.push('');
  lines.push(`**Transaction Details**`);
  if (t.paydrc_reference) lines.push(`• Reference: \`${t.paydrc_reference}\``);
  if (t.switch_reference) lines.push(`• Switch Ref: \`${t.switch_reference}\``);
  if (t.telco_reference) lines.push(`• Operator Ref: \`${t.telco_reference}\``);
  if (t.thirdparty_reference) lines.push(`• Merchant Ref: \`${t.thirdparty_reference}\``);
  if (t.customer_masked) lines.push(`• Customer: \`${t.customer_masked}\``);
  if (t.amount != null) lines.push(`• Amount: **${t.amount.toLocaleString()} ${t.currency || ''}**`);
  if (t.method) lines.push(`• Channel: ${t.method}`);
  lines.push(`• Initiated: ${initiated}`);

  if (t.status_description) {
    lines.push('');
    lines.push(`**Information**`);
    lines.push(t.status_description);
  }

  lines.push('━━━━━━━━━━━━━━━━━━━━━');
  return lines.join('\n');
};

const tryRefreshToken = async (): Promise<string | null> => {
  try {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return null;

    const response = await axios.post(
      `${API_URL}/auth/refresh`,
      {},
      {
        headers: { Authorization: `Bearer ${currentToken}` },
        timeout: 10000,
      },
    );

    const newToken = response.data.access_token;
    if (newToken) {
      localStorage.setItem('token', newToken);
      return newToken;
    }
    return null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// ChatBot component
// ---------------------------------------------------------------------------
const ChatBot: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLocalization();

  const displayName = user?.firstname
    ? user.firstname.charAt(0).toUpperCase() + user.firstname.slice(1)
    : '';

  const getQuickActions = useCallback((): QuickAction[] => [
    {
      id: 'balance',
      label: t('chat.action.balance'),
      message: t('chat.action.balanceMsg'),
      icon: <Wallet className="h-4 w-4" />,
      category: 'operations',
    },
    {
      id: 'transaction',
      label: t('chat.action.transactions'),
      message: t('chat.action.transactionsMsg'),
      icon: <CreditCard className="h-4 w-4" />,
      category: 'operations',
    },
    {
      id: 'network',
      label: t('chat.action.network'),
      message: t('chat.action.networkMsg'),
      icon: <Wifi className="h-4 w-4" />,
      category: 'analytics',
    },
    {
      id: 'transfer',
      label: t('chat.action.transfers'),
      message: t('chat.action.transfersMsg'),
      icon: <ArrowRightLeft className="h-4 w-4" />,
      category: 'operations',
    },
    {
      id: 'recent',
      label: t('chat.action.recent'),
      message: t('chat.action.recentMsg'),
      icon: <Clock className="h-4 w-4" />,
      category: 'operations',
    },
    {
      id: 'daily-summary',
      label: t('chat.insight.dailySummary'),
      message: t('chat.insight.dailySummaryMsg'),
      icon: <TrendingUp className="h-4 w-4" />,
      category: 'analytics',
    },
    {
      id: 'failed-tx',
      label: t('chat.insight.failedTx'),
      message: t('chat.insight.failedTxMsg'),
      icon: <AlertTriangle className="h-4 w-4" />,
      category: 'analytics',
    },
    {
      id: 'support',
      label: t('chat.action.support'),
      message: t('chat.action.supportMsg'),
      icon: <HeadphonesIcon className="h-4 w-4" />,
      category: 'account',
    },
    {
      id: 'help',
      label: t('chat.action.help'),
      message: t('chat.action.helpMsg'),
      icon: <HelpCircle className="h-4 w-4" />,
      category: 'account',
    },
  ], [t]);

  const getSuggestedPrompts = useCallback(() => [
    t('chat.suggest.balance'),
    t('chat.suggest.operator'),
    t('chat.suggest.failed'),
    t('chat.suggest.recent'),
  ], [t]);

  const getPostResponseActions = useCallback((responseType: string | null): ChatAction[] => {
    if (!responseType) return [];
    const actions: Record<string, ChatAction[]> = {
      balance: [
        { label: language === 'en' ? 'Go to Balances' : 'Voir les balances', href: '/balances', variant: 'primary' },
      ],
      transaction: [
        { label: language === 'en' ? 'View all transactions' : 'Voir toutes les transactions', href: '/history', variant: 'primary' },
        { label: language === 'en' ? 'Open a ticket' : 'Ouvrir un ticket', href: '/tickets?create=new', variant: 'secondary' },
      ],
      network: [
        { label: language === 'en' ? 'View Analytics' : 'Voir Analytics', href: '/analytics', variant: 'primary' },
      ],
      transfer: [
        { label: language === 'en' ? 'New transfer' : 'Nouveau transfert', href: '/transfers', variant: 'primary' },
        { label: language === 'en' ? 'View Reports' : 'Voir les rapports', href: '/reports', variant: 'secondary' },
      ],
    };
    return actions[responseType] || [];
  }, [language]);

  const buildWelcomeMessage = useCallback(
    (): Message => {
      const hour = new Date().getHours();
      let greeting: string;
      if (language === 'en') {
        greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
      } else {
        greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';
      }

      return {
        id: 'welcome',
        text:
          `${greeting}${displayName ? ` **${displayName}**` : ''} ! ${t('chat.welcome')} **${t('chat.assistantName')}**.\n\n` +
          `${t('chat.welcomeDesc')}\n\n` +
          t('chat.welcomeAction'),
        sender: 'bot',
        timestamp: new Date(),
        type: 'welcome',
      };
    },
    [displayName, t, language],
  );

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([buildWelcomeMessage()]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeCategory, setActiveCategory] = useState<'operations' | 'analytics' | 'account'>('operations');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: Message[] = JSON.parse(stored).map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
        if (parsed.length > 0) {
          setMessages(parsed);
          setShowQuickActions(false);
        }
      }
    } catch {
      // ignore corrupted data
    }
  }, []);

  useEffect(() => {
    if (messages.length > 1 || messages[0]?.id !== 'welcome') {
      try {
        const toStore = messages.slice(-MAX_STORED_MESSAGES);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
      } catch {
        // storage full
      }
    }
  }, [messages]);

  // ---------------------------------------------------------------------------
  // Auto-scroll
  // ---------------------------------------------------------------------------
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollDown(scrollHeight - scrollTop - clientHeight > 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Send message with token refresh
  // ---------------------------------------------------------------------------
  const handleSendMessage = async (text?: string) => {
    const messageText = text || inputText.trim();
    if (!messageText || isTyping) return;

    setInputText('');
    setIsTyping(true);
    setShowQuickActions(false);

    const userMessage: Message = {
      id: generateId(),
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
      type: 'text',
    };
    setMessages((prev) => [...prev, userMessage]);

    // If the input looks like a reference, route to /bot/lookup instead of /chat/message
    const isReference = looksLikeReference(messageText);

    const sendRequest = async (token: string) => {
      if (isReference) {
        return axios.get(
          `${API_URL}/bot/lookup`,
          {
            params: { ref: messageText.trim() },
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': language,
            },
            timeout: 15000,
          },
        );
      }
      return axios.post(
        `${API_URL}/chat/message`,
        { message: messageText, language },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': language,
          },
          timeout: 15000,
        },
      );
    };

    try {
      let token = localStorage.getItem('token') || '';
      let response;

      try {
        response = await sendRequest(token);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          const newToken = await tryRefreshToken();
          if (newToken) {
            token = newToken;
            response = await sendRequest(token);
          } else {
            const expiredMsg: Message = {
              id: generateId(),
              text: `**${t('chat.sessionExpired')}**\n\n${t('chat.sessionExpiredLogin')}`,
              sender: 'bot',
              timestamp: new Date(),
              type: 'session-expired',
            };
            setMessages((prev) => [...prev, expiredMsg]);
            setIsTyping(false);
            return;
          }
        } else {
          throw error;
        }
      }

      const reply = isReference
        ? formatLookupResponse(response!.data, language)
        : (response!.data.reply ||
            (language === 'en'
              ? "I couldn't process your request. Please try again."
              : "Je n'ai pas pu traiter votre demande. Veuillez reessayer."));

      let messageType: Message['type'] = 'text';
      if (reply.includes('support') || reply.includes('Support') || reply.includes('contacter') || reply.includes('Contact')) {
        messageType = 'contact';
      }

      const responseType = detectResponseType(reply);
      const postActions = getPostResponseActions(responseType);

      const botMessage: Message = {
        id: generateId(),
        text: reply,
        sender: 'bot',
        timestamp: new Date(),
        type: messageType,
        actions: postActions.length > 0 ? postActions : undefined,
      };

      setMessages((prev) => [...prev, botMessage]);

      if (!isOpen) setUnreadCount((c) => c + 1);

      setTimeout(() => {
        const needsTicket = requiresTicket(messageText, reply);
        const isFinal = responseType !== null;

        const isAskingForMore =
          reply.includes('Veuillez fournir') ||
          reply.includes('reference requise') ||
          reply.includes('Reference requise') ||
          reply.includes('Please provide') ||
          reply.includes('Reference Required');

        if (isAskingForMore) return;

        if (needsTicket) {
          const ticketMsg: Message = {
            id: generateId(),
            text:
              `**${t('chat.ticket.needHelp')}**\n\n` +
              `${t('chat.ticket.desc')}\n\n` +
              t('chat.ticket.team'),
            sender: 'bot',
            timestamp: new Date(),
            type: 'ticket-suggestion',
          };
          setMessages((prev) => [...prev, ticketMsg]);
        } else if (isFinal) {
          const closure: Message = {
            id: generateId(),
            text: t('chat.canIHelp'),
            sender: 'bot',
            timestamp: new Date(),
            type: 'text',
          };
          setMessages((prev) => [...prev, closure]);
          setShowQuickActions(true);
        } else {
          setShowQuickActions(true);
        }
      }, 800);
    } catch {
      const errorMsg: Message = {
        id: generateId(),
        text: `**${t('chat.connectionError')}**\n\n${t('chat.connectionErrorDesc')}`,
        sender: 'bot',
        timestamp: new Date(),
        type: 'text',
      };
      setMessages((prev) => [...prev, errorMsg]);
      setShowQuickActions(true);
    } finally {
      setIsTyping(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleQuickAction = (action: QuickAction) => {
    handleSendMessage(action.message);
  };

  const handleSuggestedPrompt = (prompt: string) => {
    handleSendMessage(prompt);
  };

  const handleNavigate = (href: string) => {
    window.location.href = href;
  };

  const handleCreateTicket = () => {
    window.location.href = '/tickets?create=new';
  };

  const handleResetConversation = () => {
    setMessages([buildWelcomeMessage()]);
    setShowQuickActions(true);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) =>
    new Date(date).toLocaleTimeString(language === 'en' ? 'en-US' : 'fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });

  // ---------------------------------------------------------------------------
  // Action button renderer (shared)
  // ---------------------------------------------------------------------------
  const renderActionButtons = (actions: ChatAction[]) => (
    <div className="flex gap-2 mt-3">
      {actions.map((action, i) => (
        <button
          key={i}
          onClick={() => action.href && handleNavigate(action.href)}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200
            flex items-center justify-center gap-1.5 ${
              action.variant === 'primary'
                ? 'bg-white text-[#000065] hover:bg-white/90'
                : 'bg-white/10 text-white/90 hover:bg-white/20 border border-white/10'
            }`}
        >
          <ChevronRight className="h-3 w-3" />
          {action.label}
        </button>
      ))}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render message content
  // ---------------------------------------------------------------------------
  const renderMessageContent = (message: Message) => {
    // Welcome message
    if (message.type === 'welcome') {
      return (
        <div>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#63d400] to-[#4db300]">
              <Sparkles className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">{t('chat.assistantName')}</h3>
              <p className="text-[11px] text-white/50">{t('chat.assistantSubtitle')}</p>
            </div>
          </div>
          <ReactMarkdown
            components={{
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
              p: ({ children }) => (
                <p className="text-white/80 leading-relaxed mb-2 text-[13px]">{children}</p>
              ),
            }}
          >
            {message.text}
          </ReactMarkdown>
        </div>
      );
    }

    // Session expired
    if (message.type === 'session-expired') {
      return (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <RefreshCw className="h-4 w-4 text-amber-400" />
            <h3 className="font-semibold text-amber-400 text-sm">{t('chat.sessionExpired')}</h3>
          </div>
          <ReactMarkdown
            components={{
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
              p: ({ children }) => (
                <p className="text-white/70 leading-relaxed mb-2 text-[13px]">{children}</p>
              ),
            }}
          >
            {message.text}
          </ReactMarkdown>
          <button
            onClick={() => { window.location.href = '/login'; }}
            className="w-full mt-3 bg-white text-[#000065] px-4 py-2.5 rounded-lg text-xs font-bold
                       hover:bg-white/90 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {language === 'en' ? 'Log in again' : 'Se reconnecter'}
          </button>
        </div>
      );
    }

    // Ticket suggestion
    if (message.type === 'ticket-suggestion') {
      return (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Ticket className="h-3.5 w-3.5 text-blue-400" />
            </div>
            <h3 className="font-semibold text-white text-sm">{t('chat.ticket.title')}</h3>
          </div>
          <ReactMarkdown
            components={{
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
              p: ({ children }) => (
                <p className="text-white/70 leading-relaxed mb-2 text-[13px]">{children}</p>
              ),
            }}
          >
            {message.text}
          </ReactMarkdown>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleCreateTicket}
              className="flex-1 bg-white text-[#000065] px-3 py-2.5 rounded-lg text-xs font-bold
                         hover:bg-white/90 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Ticket className="h-3.5 w-3.5" />
              {t('chat.ticket.open')}
            </button>
            <button
              onClick={() => setShowQuickActions(true)}
              className="flex-1 bg-white/10 text-white/90 px-3 py-2.5 rounded-lg text-xs font-semibold
                         hover:bg-white/20 transition-all duration-200 border border-white/10"
            >
              {t('chat.anotherQuestion')}
            </button>
          </div>
        </div>
      );
    }

    // Transaction overview (structured card)
    if (isTransactionMessage(message.text)) {
      const tip = extractTip(message.text);
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="h-4 w-4 text-white/60" />
            <h3 className="font-bold text-white text-sm">
              {language === 'en' ? 'Transaction Details' : 'Detail de la Transaction'}
            </h3>
          </div>

          <div className="bg-white/5 rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
            {['Method:', 'Amount:', 'Status:', 'Description:'].map((field) => (
              <div key={field} className="flex justify-between items-center px-4 py-2.5">
                <span className="text-white/50 text-xs">{field.replace(':', '')}</span>
                <span
                  className={`font-medium text-xs ${
                    field === 'Status:'
                      ? `px-2.5 py-1 rounded-full border text-[11px] font-semibold ${getStatusStyles(message.text)}`
                      : field === 'Amount:'
                      ? 'text-white font-bold'
                      : 'text-white/90'
                  }`}
                >
                  {extractTransactionField(message.text, field)}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-white/5 rounded-xl border border-white/10 p-4">
            <h4 className="font-semibold text-white/70 mb-2 text-xs uppercase tracking-wider">References</h4>
            <div className="space-y-1.5">
              {['Customer:', 'Merchant:', 'PayDRC:', 'Telco:'].map((ref) => (
                <div key={ref} className="flex justify-between items-start text-[11px]">
                  <span className="text-white/40">{ref.replace(':', '')}</span>
                  <span className="text-white/80 text-right break-all max-w-[180px] font-mono">
                    {extractTransactionField(message.text, ref)}
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/5 mt-3 pt-2 text-[11px] text-white/40">
              {language === 'en' ? 'Updated' : 'Mis a jour'} : {extractTransactionField(message.text, 'Last Updated:')}
            </div>
          </div>

          {tip && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-xs text-blue-300">
              {tip}
            </div>
          )}

          {message.actions && message.actions.length > 0 && renderActionButtons(message.actions)}
        </div>
      );
    }

    // Default markdown rendering
    return (
      <div>
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h1 className="text-sm font-bold text-white mb-2 pb-2 border-b border-white/10">
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-[13px] font-bold text-white mt-3 mb-1.5">{children}</h2>
            ),
            p: ({ children }) => (
              <p className="text-[13px] leading-relaxed text-white/80 whitespace-pre-line mb-2">
                {children}
              </p>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-white">{children}</strong>
            ),
            code: ({ children }) => (
              <code className="bg-white/10 text-[#63d400] px-1.5 py-0.5 rounded text-xs font-mono">
                {children}
              </code>
            ),
            ul: ({ children }) => (
              <ul className="text-[13px] text-white/80 ml-1 mb-2 space-y-1">{children}</ul>
            ),
            li: ({ children }) => (
              <li className="flex items-start gap-2">
                <span className="text-[#63d400] mt-0.5 text-xs">&#9679;</span>
                <span>{children}</span>
              </li>
            ),
            table: ({ children }) => (
              <div className="overflow-x-auto my-3 rounded-xl border border-white/10">
                <table className="w-full text-xs">{children}</table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-white/5">{children}</thead>
            ),
            th: ({ children }) => (
              <th className="text-left px-3 py-2 text-white/60 font-semibold border-b border-white/10 text-[11px] uppercase tracking-wider">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-3 py-2 text-white/80 border-b border-white/5 text-xs">
                {children}
              </td>
            ),
            hr: () => <hr className="my-2 border-white/10" />,
          }}
        >
          {message.text}
        </ReactMarkdown>

        {message.actions && message.actions.length > 0 && renderActionButtons(message.actions)}
      </div>
    );
  };

  // Category labels
  const categoryLabels = {
    operations: language === 'en' ? 'Operations' : 'Operations',
    analytics: language === 'en' ? 'Analytics' : 'Analytics',
    account: language === 'en' ? 'Account' : 'Compte',
  };

  const quickActions = getQuickActions();
  const filteredActions = quickActions.filter((a) => a.category === activeCategory);
  const suggestedPrompts = getSuggestedPrompts();

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-50 group"
          aria-label={t('chat.openAssistant')}
        >
          <div
            className="w-14 h-14 rounded-2xl shadow-lg shadow-[#000065]/30 flex items-center justify-center
                       transition-all duration-300 group-hover:shadow-xl group-hover:scale-105 group-hover:shadow-[#000065]/40"
            style={{ background: 'linear-gradient(135deg, #000065, #0a0a7a)' }}
          >
            <MessageCircle className="h-6 w-6 text-white" />
          </div>
          {unreadCount > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-[10px]
                         font-bold flex items-center justify-center shadow-lg"
              style={{ backgroundColor: '#63d400' }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 w-[360px] lg:w-[420px]
                     h-[560px] lg:h-[650px] rounded-2xl shadow-2xl shadow-black/30
                     z-50 flex flex-col overflow-hidden animate-scale-in"
          style={{ background: '#0a0a2e' }}
        >
          {/* Header */}
          <div
            className="px-5 py-4 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, #000065, #0a0a7a)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-[15px] tracking-tight">{t('chat.assistantName')}</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#63d400' }} />
                  <p className="text-xs text-white/60 font-medium">{t('chat.online')}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleResetConversation}
                className="text-white/50 hover:text-white hover:bg-white/10 p-2 rounded-xl
                           transition-all duration-200"
                title={t('chat.newConversation')}
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/50 hover:text-white hover:bg-white/10 p-2 rounded-xl
                           transition-all duration-200"
                title={t('chat.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
            style={{ background: 'linear-gradient(180deg, #0a0a2e 0%, #0d0d35 100%)' }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-end gap-2 ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                } animate-fade-in`}
              >
                {message.sender === 'bot' && (
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border border-white/10"
                    style={{ background: 'linear-gradient(135deg, #000065, #1a1a8e)' }}
                  >
                    <Bot className="h-3.5 w-3.5 text-white/80" />
                  </div>
                )}

                <div className={`max-w-[82%] ${message.sender === 'user' ? 'order-1' : ''}`}>
                  <div
                    className={`p-4 rounded-2xl ${
                      message.sender === 'user'
                        ? 'rounded-br-md'
                        : message.type === 'ticket-suggestion'
                        ? 'rounded-bl-md border border-blue-500/20'
                        : message.type === 'session-expired'
                        ? 'rounded-bl-md border border-amber-500/20'
                        : 'rounded-bl-md border border-white/[0.06]'
                    }`}
                    style={
                      message.sender === 'user'
                        ? { background: 'linear-gradient(135deg, #000065, #1a1a8e)' }
                        : { background: 'rgba(255,255,255,0.04)' }
                    }
                  >
                    {message.sender === 'user' ? (
                      <p className="text-[13px] text-white leading-relaxed">{message.text}</p>
                    ) : (
                      renderMessageContent(message)
                    )}

                    {message.type === 'contact' && message.text.includes('support@gofreshpay.com') && (
                      <div className="mt-3 p-3 bg-white/5 rounded-xl border border-white/10">
                        <p className="text-xs font-medium text-white/60 flex items-center gap-1.5 mb-1">
                          <HeadphonesIcon className="h-3.5 w-3.5" />
                          {t('chat.contactDirect')}
                        </p>
                        <a
                          href="mailto:support@gofreshpay.com"
                          className="text-xs text-[#63d400] hover:underline font-medium"
                        >
                          support@gofreshpay.com
                        </a>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-white/25 mt-1 px-1">
                    {formatTime(message.timestamp)}
                  </p>
                </div>

                {message.sender === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 border border-white/10">
                    <User className="h-3.5 w-3.5 text-white/60" />
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex items-end gap-2 animate-fade-in">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border border-white/10"
                  style={{ background: 'linear-gradient(135deg, #000065, #1a1a8e)' }}
                >
                  <Bot className="h-3.5 w-3.5 text-white/80" />
                </div>
                <div className="bg-white/[0.04] border border-white/[0.06] p-4 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-white/30 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <div className="w-2 h-2 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </div>
                    <span className="text-xs text-white/40 font-medium">{t('chat.analyzing')}</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Scroll to bottom */}
          {showScrollDown && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-40 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm
                         border border-white/10 rounded-full p-2
                         hover:bg-white/20 transition-all duration-200 z-10"
            >
              <ArrowDown className="h-3.5 w-3.5 text-white/60" />
            </button>
          )}

          {/* Suggested prompts (only at start) */}
          {showQuickActions && !isTyping && messages.length <= 3 && (
            <div className="px-4 pt-3 pb-2 border-t border-white/[0.06]"
                 style={{ background: '#0d0d35' }}>
              <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest mb-2">
                {t('chat.suggestions')}
              </p>
              <div className="space-y-1.5">
                {suggestedPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestedPrompt(prompt)}
                    className="w-full text-left px-3.5 py-2.5 text-[13px] text-white/90 font-medium
                               bg-white/[0.04] rounded-xl hover:bg-white/[0.08]
                               transition-all duration-200 border border-white/[0.06]
                               hover:border-white/10 flex items-center gap-2.5"
                  >
                    <Zap className="h-3.5 w-3.5 text-[#63d400] shrink-0" />
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick actions with category tabs */}
          {showQuickActions && !isTyping && (
            <div className="px-4 py-3 border-t border-white/[0.06]"
                 style={{ background: '#0d0d35' }}>
              {/* Category tabs */}
              <div className="flex items-center gap-1.5 mb-2.5">
                <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest mr-auto">
                  {t('chat.quickActions')}
                </p>
                <div className="flex gap-1">
                  {(['operations', 'analytics', 'account'] as const).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all duration-200 ${
                        activeCategory === cat
                          ? 'bg-white text-[#000065]'
                          : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.1] hover:text-white/60'
                      }`}
                    >
                      {categoryLabels[cat]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtered action buttons */}
              <div className="flex flex-wrap gap-2">
                {filteredActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action)}
                    className="flex items-center gap-2 px-3.5 py-2 bg-white/[0.04]
                               border border-white/[0.08] rounded-xl text-xs
                               font-semibold text-white/70
                               hover:bg-white/[0.08] hover:text-white hover:border-white/15
                               transition-all duration-200"
                  >
                    <span className="text-white/40">{action.icon}</span>
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="px-4 py-3 border-t border-white/[0.06]"
               style={{ background: '#08082a' }}>
            <div className="flex items-center gap-2.5">
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('chat.placeholder')}
                className="flex-1 px-4 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl
                           text-sm text-white placeholder-white/30
                           focus:ring-2 focus:ring-[#63d400]/30 focus:border-[#63d400]/40
                           transition-all duration-200 outline-none"
                disabled={isTyping}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputText.trim() || isTyping}
                className="p-3 rounded-xl text-white transition-all duration-200
                           disabled:opacity-20 disabled:cursor-not-allowed
                           hover:opacity-90 active:scale-95"
                style={{ backgroundColor: '#63d400' }}
                aria-label={t('chat.send')}
              >
                <Send className="h-4 w-4 text-white" />
              </button>
            </div>
            <p className="text-[10px] text-white/20 text-center mt-2 font-medium">
              {t('chat.assistantName')} — {t('chat.poweredBy')}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
